import React, { useMemo, useState } from 'react';
import {
 Button, Card, Dropdown, IMenuProps, Tooltip,
} from '@revfluence/fresh';
import { Typography } from '@revfluence/fresh';
import { AngleDownIcon, BoxesStackedIcon } from '@revfluence/fresh-icons/regular/esm';
import { BagShoppingIcon } from '@revfluence/fresh-icons/regular/esm';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';
import styles from './EmptyScreen.scss';
import { useProductsContext } from '../../Products/ProductsContext';
import { CSVCollectionUploadModal } from '../../../NonShopifyPFA/CSVCollectionUploadModal';
import { useImportedCollectionType } from '../../context/ImportedCollectionTypeContext';

const { Text, Title } = Typography;

export const EmptyScreen = () => {
  const { isShopifyConnected, setIsOpen, setIsFindAndImportOpen } = useProductsContext();
  const [isProductUploadModalOpen, setIsProductUploadModalOpen] = useState(false);

  // Check feature flag for non-Shopify PFA
  const nonShopifyPfaEnabled = useClientFeatureEnabled(ClientFeature.NON_SHOPIFY_PFA);

  // Get imported collection type from context
  const { importedCollectionType: contextImportedCollectionType } = useImportedCollectionType();

  // Override collection type based on feature flag
  const importedCollectionType = nonShopifyPfaEnabled ? contextImportedCollectionType : 'SHOPIFY';

  const items = useMemo<IMenuProps['items']>(() => {
    const allItems = [
      // Only show Shopify options if Shopify is connected
      ...(isShopifyConnected ? [
        {
          key: 'import',
          label: 'Import Collections',
          onClick: () => setIsOpen(true),
        },
        {
          key: 'findAndImport',
          label: 'Find and Import',
          onClick: () => setIsFindAndImportOpen(true),
        },
      ] : []),
      ...(nonShopifyPfaEnabled ? [{
        key: 'uploadCsv',
        label: 'Upload CSV',
        onClick: () => {
          setIsProductUploadModalOpen(true);
        },
      }] : []),
    ];

    // If feature flag is disabled, only show Shopify options (and only if connected)
    if (!nonShopifyPfaEnabled) {
      return allItems.filter((item) => (item.key === 'import' || item.key === 'findAndImport') && isShopifyConnected);
    }

    // Filter items based on importedCollectionType when feature flag is enabled
    if (importedCollectionType === 'CSV') {
      // Only show Upload CSV option
      return allItems.filter((item) => item.key === 'uploadCsv');
    } else if (importedCollectionType === 'SHOPIFY') {
      // Only show Shopify options (Import Collections and Find and Import) if connected
      return allItems.filter((item) => (item.key === 'import' || item.key === 'findAndImport') && isShopifyConnected);
    }

    // If null or loading, show all options (when feature flag is enabled) but respect Shopify connection
    return allItems;
  }, [setIsFindAndImportOpen, setIsOpen, setIsProductUploadModalOpen, importedCollectionType, nonShopifyPfaEnabled, isShopifyConnected]);

  return (
    <div className={styles.NoCollections}>
      <Card>
        <div>
          <BoxesStackedIcon fontSize={80} className={styles.stackedBoxesIcon} />
          <div className={styles.titleContainer}>
            <Title level={5}>Start importing your products</Title>
            <Text>Easily import products from Shopify or CSV</Text>
            {nonShopifyPfaEnabled && (
              <Text type="secondary" className="mt-2">
                You can either go ahead with Upload CSV or Import Collections from Shopify. You cannot do both.
              </Text>
            )}
          </div>
          {/* We will need the following in the next iteration, do not delete */}
          {/* <div className={styles.importOptionsContainer}>
            <Card className={styles.uploadCard} onClick={() => setIsOpen(true)}>
              <div>
                <ShopifyIcon fontSize={32} />
                <Text weight="semibold">Import Shopify Collections</Text>
                <Text type="secondary" color="var(--gray-7)">
                  Create a new object to become more powerful.
                </Text>
              </div>
            </Card>
            <Card className={styles.uploadCard}>
              <div>
                <FileCsvIcon fontSize={32} />
                <Text weight="semibold">Upload Collection via CSV</Text>
                <Text type="secondary" color="var(--gray-7)">
                  Create a new object to become more powerful.
                </Text>
              </div>
            </Card>
          </div> */}
          <Tooltip title={isShopifyConnected || nonShopifyPfaEnabled ? '' : 'Please connect a Shopify store to start importing products'}>
            <Dropdown menu={{ items }} disabled={!isShopifyConnected && !nonShopifyPfaEnabled}>
              <Button
                type="primary"
                icon={<BagShoppingIcon />}
                disabled={!isShopifyConnected && !nonShopifyPfaEnabled}
              >
                Import Products
                <AngleDownIcon />
              </Button>
            </Dropdown>
          </Tooltip>
        </div>
      </Card>
      {nonShopifyPfaEnabled && (
        <CSVCollectionUploadModal isProductUploadModalOpen={isProductUploadModalOpen} setIsProductUploadModalOpen={setIsProductUploadModalOpen} />
      )}
    </div>
  );
};
